import { useState } from 'react';
import { Copy } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import type { VirtualAccountSummary } from '@/types/wallet';

interface FundWalletModalProps {
  open: boolean;
  onClose: () => void;
  account: VirtualAccountSummary | null;
  chargePercent?: number;
  chargeCap?: number;
  fundingEnabled?: boolean;
  generating?: boolean;
  onGenerate?: () => void;
}

function formatFeePercent(value: number) {
  const n = Number(value);
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
}

function feeSummary(chargePercent: number, chargeCap: number) {
  if (chargePercent <= 0) return 'No funding fee';
  return `${formatFeePercent(chargePercent)}% fee (max ₦${chargeCap.toLocaleString()} per transfer)`;
}

export function FundWalletModal({
  open,
  onClose,
  account,
  chargePercent = 0,
  chargeCap = 0,
  fundingEnabled = true,
  generating = false,
  onGenerate,
}: FundWalletModalProps) {
  const [copied, setCopied] = useState<string | null>(null);

  async function copy(text: string, field: string) {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }

  const bankLabel = account
    ? (account.bankName.split('/')[0]?.trim() ?? account.bankName)
    : '';

  const rows = account
    ? [
        { label: 'Bank', value: bankLabel, key: 'bank' },
        { label: 'Account number', value: account.accountNumber, key: 'number', copy: true },
        { label: 'Account name', value: account.accountName, key: 'name', copy: true },
      ]
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fund your wallet"
      description="Transfer to your PalmPay account. Wallet credits automatically."
      size="sm"
    >
      {!account ? (
        <div className="space-y-3">
          <p className="text-sm text-brand-muted">
            Generate a virtual account to fund your wallet via bank transfer.
          </p>
          {!fundingEnabled && (
            <p className="text-sm text-brand-muted">Wallet funding is temporarily unavailable.</p>
          )}
          <Button
            fullWidth
            loading={generating}
            loadingText="Generating..."
            disabled={!fundingEnabled}
            onClick={onGenerate}
          >
            Generate virtual account
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-brand-surface divide-y divide-gray-100">
            {rows.map((row) => (
              <div key={row.key} className="flex items-start justify-between gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-brand-muted">{row.label}</p>
                  <p
                    className={
                      row.key === 'number'
                        ? 'font-mono text-sm font-bold tracking-wide text-brand-navy'
                        : 'break-words text-sm font-semibold leading-snug text-brand-navy'
                    }
                  >
                    {row.value}
                  </p>
                </div>
                {row.copy && (
                  <button
                    type="button"
                    onClick={() => void copy(row.value, row.key)}
                    aria-label={`Copy ${row.label.toLowerCase()}`}
                    className="mt-0.5 shrink-0 rounded-md p-1.5 text-brand-muted transition hover:bg-white hover:text-brand-navy"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {copied && <p className="text-xs font-medium text-secondary">Copied!</p>}

          <p className="text-xs leading-relaxed text-brand-muted">
            {feeSummary(chargePercent, chargeCap)} · Min ₦100 · usually under 2 minutes
          </p>
        </div>
      )}

      {generating && (
        <div className="mt-3 flex justify-center">
          <Loader label="Creating account" />
        </div>
      )}
    </Modal>
  );
}
